import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';

export interface PowerUpStatus {
  [key: string]: boolean;
}

export function usePowerUps(appSlug: string) {
  const businessContext = useBusiness();
  const selectedBusiness = businessContext?.selectedBusiness;
  
  const [powerUps, setPowerUps] = useState<PowerUpStatus>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedBusiness?.id) {
      setLoading(false);
      return;
    }

    fetchPowerUps();
  }, [selectedBusiness?.id, appSlug]);

  const fetchPowerUps = async () => {
    try {
      console.log('Fetching power-ups for app:', appSlug, 'business:', selectedBusiness.id);
      
      // Call the backend function to get all power-ups for this app
      const { data, error } = await supabase
        .rpc('get_business_features', {
          p_business_id: selectedBusiness.id,
          p_app_slug: appSlug
        });

      if (error) {
        console.error('Error fetching power-ups:', error);
        setPowerUps({});
      } else {
        console.log('Power-ups fetched:', data);
        setPowerUps(data || {});
      }
    } catch (error) {
      console.error('Failed to fetch power-ups:', error);
      setPowerUps({});
    } finally {
      setLoading(false);
    }
  };

  const isPowerUpEnabled = (powerUpSlug: string): boolean => {
    return powerUps[powerUpSlug] === true;
  };

  const togglePowerUp = async (powerUpSlug: string, enabled: boolean) => {
    try {
      // First get the power-up ID
      const { data: powerUpData, error: powerUpError } = await supabase
        .from('power_ups_registry_v2')
        .select('id')
        .eq('slug', powerUpSlug)
        .single();

      if (powerUpError || !powerUpData) {
        console.error('Power-up not found:', powerUpSlug);
        return false;
      }

      // Check if record exists
      const { data: existing } = await supabase
        .from('business_power_ups_v2')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .eq('power_up_id', powerUpData.id)
        .single();

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('business_power_ups_v2')
          .update({
            enabled,
            enabled_at: enabled ? new Date().toISOString() : null,
            disabled_at: !enabled ? new Date().toISOString() : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) {
          console.error('Error updating power-up status:', error);
          return false;
        }
      } else {
        // Create new record
        const { error } = await supabase
          .from('business_power_ups_v2')
          .insert({
            business_id: selectedBusiness.id,
            power_up_id: powerUpData.id,
            enabled,
            enabled_at: enabled ? new Date().toISOString() : null
          });

        if (error) {
          console.error('Error creating power-up status:', error);
          return false;
        }
      }

      // Refresh power-ups
      await fetchPowerUps();
      return true;
    } catch (error) {
      console.error('Failed to toggle power-up:', error);
      return false;
    }
  };

  return {
    powerUps,
    loading,
    isPowerUpEnabled,
    togglePowerUp,
    refetch: fetchPowerUps
  };
}
